import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, Switch, Space, message } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import firebase from "firebase/compat/app";
import { auditedDelete, newOpId, stamp } from "../../../modules/helpers/audit";

export default function ReturnAddresses({ companyId }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const db = firebase.firestore();

  function fetchAddresses() {
    if (!companyId) return;
    setLoading(true);
    db.collection("companies")
      .doc(companyId)
      .collection("return_addresses")
      .orderBy("created_at", "desc")
      .get()
      .then((snap) => {
        setAddresses(
          snap.docs.map((doc) => ({ id: doc.id, key: doc.id, ...doc.data() }))
        );
      })
      .catch((e) => {
        console.error("Error fetching return addresses:", e);
        message.error("Erreur lors du chargement des adresses");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchAddresses();
  }, [companyId]);

  function openAdd() {
    setEditing(null);
    form.resetFields();
    setModalVisible(true);
  }

  function openEdit(record) {
    setEditing(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  }

  function handleSubmit() {
    form.validateFields().then((values) => {
      const ref = db.collection("companies").doc(companyId).collection("return_addresses");

      if (editing) {
        ref
          .doc(editing.id)
          .update({
            ...values,
            updated_at: firebase.firestore.FieldValue.serverTimestamp(),
            last_change: stamp(),
          })
          .then(() => {
            message.success("Adresse modifiée");
            setModalVisible(false);
            fetchAddresses();
          });
      } else {
        ref
          .add({
            ...values,
            is_default: addresses.length === 0,
            created_at: firebase.firestore.FieldValue.serverTimestamp(),
            updated_at: firebase.firestore.FieldValue.serverTimestamp(),
            last_change: stamp(),
          })
          .then(() => {
            message.success("Adresse ajoutée");
            setModalVisible(false);
            fetchAddresses();
          });
      }
    });
  }

  function handleDelete(record) {
    Modal.confirm({
      title: "Supprimer cette adresse ?",
      content: `"${record.label}" sera supprimée définitivement.`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () => {
        auditedDelete(
          db.collection("companies").doc(companyId).collection("return_addresses").doc(record.id)
        )
          .then(() => {
            message.success("Adresse supprimée");
            fetchAddresses();
          })
          .catch((e) => {
            console.error(e);
            message.error("Une erreur est survenue. Veuillez réessayer.");
          });
      },
    });
  }

  function handleSetDefault(record) {
    const batch = db.batch();
    const ref = db.collection("companies").doc(companyId).collection("return_addresses");
    const op = newOpId();

    addresses.forEach((addr) => {
      batch.update(ref.doc(addr.id), { is_default: addr.id === record.id, last_change: stamp({ op }) });
    });

    batch.commit().then(() => {
      message.success("Adresse par défaut mise à jour");
      fetchAddresses();
    });
  }

  if (!companyId) {
    return <p style={{ color: "#999" }}>Enregistrez le compte avant d'ajouter des adresses de retour.</p>;
  }

  const columns = [
    { title: "Libellé", dataIndex: "label", key: "label" },
    { title: "Nom", dataIndex: "name", key: "name" },
    {
      title: "Adresse",
      key: "address",
      render: (_, r) => (
        <>
          {r.address_line_1}
          {r.address_line_2 ? `, ${r.address_line_2}` : ""} — {r.postal_code} {r.city}
        </>
      ),
    },
    {
      title: "Par défaut",
      key: "default",
      width: 100,
      render: (_, r) => (
        <Switch
          checked={r.is_default}
          size="small"
          onChange={() => handleSetDefault(r)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Button size="small" icon={<DeleteOutlined />} danger onClick={() => handleDelete(r)} />
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
        <span style={{ fontWeight: 500 }}>Adresses de retour pour les liasses AR</span>
        <Button size="small" icon={<PlusOutlined />} onClick={openAdd}>
          Ajouter
        </Button>
      </div>

      <Table
        dataSource={addresses}
        columns={columns}
        loading={loading}
        pagination={false}
        size="small"
        locale={{ emptyText: "Aucune adresse de retour" }}
      />

      <Modal
        title={editing ? "Modifier l'adresse" : "Ajouter une adresse de retour"}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        okText={editing ? "Modifier" : "Ajouter"}
        cancelText="Annuler"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="label" label="Libellé" rules={[{ required: true, message: "Requis" }]}>
            <Input placeholder="Ex: Siège social" />
          </Form.Item>
          <Form.Item name="name" label="Nom" rules={[{ required: true, message: "Requis" }]}>
            <Input placeholder="Ex: Hôpital Américain" />
          </Form.Item>
          <Form.Item name="address_line_1" label="Adresse" rules={[{ required: true, message: "Requis" }]}>
            <Input placeholder="Ex: 63 Bd Victor Hugo" />
          </Form.Item>
          <Form.Item name="address_line_2" label="Complément">
            <Input />
          </Form.Item>
          <Input.Group compact>
            <Form.Item name="postal_code" label="Code postal" rules={[{ required: true, message: "Requis" }]} style={{ width: "30%", marginRight: 8 }}>
              <Input placeholder="92200" />
            </Form.Item>
            <Form.Item name="city" label="Ville" rules={[{ required: true, message: "Requis" }]} style={{ width: "65%" }}>
              <Input placeholder="Neuilly-sur-Seine" />
            </Form.Item>
          </Input.Group>
        </Form>
      </Modal>
    </>
  );
}
